export interface ApiEnvelope<T> {
  ok: true;
  data: T;
  meta?: Record<string, unknown>;
}

export interface ApiFailure {
  ok: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;

  constructor(status: number, failure: ApiFailure) {
    super(failure.error.message);
    this.status = status;
    this.code = failure.error.code;
    this.details = failure.error.details;
  }
}

async function readJson(response: Response) {
  const text = await response.text();
  return text ? JSON.parse(text) as ApiEnvelope<unknown> | ApiFailure : { ok: true, data: null };
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path);
  const body = await readJson(response);
  if (!response.ok || !body.ok) throw new ApiError(response.status, body as ApiFailure);
  return (body as ApiEnvelope<T>).data;
}

export async function apiGetEnvelope<T>(path: string): Promise<ApiEnvelope<T>> {
  const response = await fetch(path);
  const body = await readJson(response);
  if (!response.ok || !body.ok) throw new ApiError(response.status, body as ApiFailure);
  return body as ApiEnvelope<T>;
}

export async function apiSend<T>(path: string, method: "POST" | "PATCH" | "DELETE", body?: unknown, headers?: HeadersInit): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: { "content-type": "application/json", ...headers },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = await readJson(response);
  if (!response.ok || !payload.ok) throw new ApiError(response.status, payload as ApiFailure);
  return (payload as ApiEnvelope<T>).data;
}

export async function login(password: string) {
  const response = await fetch("/admin/api/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const body = await readJson(response);
  if (!response.ok || !body.ok) throw new ApiError(response.status, body as ApiFailure);
}

export function encodePk(pk: Record<string, unknown>): string {
  return btoa(JSON.stringify(pk)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function pretty(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2);
}

export function parseJsonObject(text: string): Record<string, unknown> {
  const value = JSON.parse(text) as unknown;
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Expected a JSON object.");
  return value as Record<string, unknown>;
}
